import React from 'react';
import { Truck, Clock, DollarSign, FileText, ShieldCheck, ChevronRight } from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';
import { useLanguage } from '../../contexts/LanguageContext';

function LogisticsPage() {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-20 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Логистика и таможенное' : 
                 language === 'en' ? 'Logistics and Customs' : 
                 '物流和海关'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                {language === 'ru' ? 'оформление грузов' : 
                 language === 'en' ? 'Clearance Services' : 
                 '清关服务'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              {language === 'ru' 
                ? 'Надежная доставка грузов из Китая с оптимальным соотношением скорости и стоимости'
                : language === 'en' 
                ? 'Reliable cargo delivery from China with the optimal balance of speed and cost'
                : '从中国可靠的货物运输，速度和成本的最佳平衡'}
            </p>
          </div>
        </div>
      </section>

      {/* CARGO Introduction */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              {language === 'ru' ? 'Доставка CARGO' : 
               language === 'en' ? 'CARGO Delivery' : 
               'CARGO 运输'}
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              {language === 'ru' 
                ? <>Доставка в формате <span className="text-[#1B3BA0] font-semibold">CARGO</span> — популярный и проверенный способ ввоза товаров, которым пользуются более 95% клиентов. Это удобное решение для малого и среднего бизнеса.</>
                : language === 'en'
                ? <>Delivery in <span className="text-[#1B3BA0] font-semibold">CARGO</span> format is a popular and proven method of importing goods used by more than 95% of clients. It's a convenient solution for small and medium-sized businesses.</>
                : <><span className="text-[#1B3BA0] font-semibold">CARGO</span> 格式的交付是一种流行且经过验证的进口商品方法，超过95%的客户使用。这是中小型企业的便捷解决方案。</>
              }
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <Clock className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Быстрая доставка' : 
                          language === 'en' ? 'Fast Delivery' : 
                          '快速交付',
                  description: language === 'ru' ? 'От 10 до 25 дней в зависимости от тарифа' : 
                               language === 'en' ? 'From 10 to 25 days depending on the tariff' : 
                               '根据费率从10到25天'
                },
                {
                  icon: <DollarSign className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Низкая стоимость' : 
                          language === 'en' ? 'Low Cost' : 
                          '低成本',
                  description: language === 'ru' ? 'Выгоднее официальной доставки' : 
                               language === 'en' ? 'More profitable than official delivery' : 
                               '比官方交付更有利'
                },
                {
                  icon: <FileText className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Минимум документов' : 
                          language === 'en' ? 'Minimum Documents' : 
                          '最少文件',
                  description: language === 'ru' ? 'Не требуется оформление со стороны клиента' : 
                               language === 'en' ? 'No paperwork required from the client' : 
                               '客户无需办理手续'
                },
                {
                  icon: <ShieldCheck className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Безопасность' : 
                          language === 'en' ? 'Security' : 
                          '安全',
                  description: language === 'ru' ? 'Работаем только с надёжными каналами' : 
                               language === 'en' ? 'We work only with reliable channels' : 
                               '我们只与可靠的渠道合作'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Official Import */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Белая доставка (официальный ввоз)' : 
             language === 'en' ? 'White Delivery (Official Import)' : 
             '白色交付（官方进口）'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-[#1B3BA0] mb-6">
                {language === 'ru' ? 'Преимущества' : 
                 language === 'en' ? 'Advantages' : 
                 '优势'}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <FileText className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? 'Полный пакет документов: ГТД, инвойс, упаковочный лист, сертификаты'
                      : language === 'en' 
                      ? 'Complete package of documents: customs declaration, invoice, packing list, certificates'
                      : '完整的文件包：海关申报，发票，装箱单，证书'}
                  </span>
                </li>
                <li className="flex items-start">
                  <ShieldCheck className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? 'Подходит для компаний, которым требуется легальный учёт товара'
                      : language === 'en' 
                      ? 'Suitable for companies that require legal accounting of goods'
                      : '适合需要合法记账的公司'}
                  </span>
                </li>
                <li className="flex items-start">
                  <Truck className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? 'Возможность участия в тендерах и госзакупках'
                      : language === 'en' 
                      ? 'Opportunity to participate in tenders and government procurement'
                      : '参与招标和政府采购的机会'}
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-[#1B3BA0] mb-6">
                {language === 'ru' ? 'Особенности' : 
                 language === 'en' ? 'Features' : 
                 '特点'}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <DollarSign className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? 'Стоимость выше из-за таможенных пошлин и НДС'
                      : language === 'en' 
                      ? 'Higher cost due to customs duties and VAT'
                      : '由于关税和增值税，成本更高'}
                  </span>
                </li>
                <li className="flex items-start">
                  <Clock className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? <>
                          Сроки доставки:
                          <br />— Авто: от 10 до 25 дней
                          <br />— Морем: от 18 до 50 дней
                        </>
                      : language === 'en' 
                      ? <>
                          Delivery times:
                          <br />— By road: from 10 to 25 days
                          <br />— By sea: from 18 to 50 days
                        </>
                      : <>
                          交付时间：
                          <br />— 陆路：10至25天
                          <br />— 海运：18至50天
                        </>
                    }
                  </span>
                </li>
                <li className="flex items-start">
                  <FileText className="w-5 h-5 text-[#1B3BA0] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-600">
                    {language === 'ru' 
                      ? 'Полное таможенное оформление и сертификация'
                      : language === 'en' 
                      ? 'Complete customs clearance and certification'
                      : '完整的海关清关和认证'}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' 
              ? 'Мы поможем выбрать оптимальный вариант'
              : language === 'en' 
              ? 'We will help you choose the optimal option'
              : '我们将帮助您选择最佳选项'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Подберём логистику под ваши задачи — быстро и выгодно или официально и с документами'
              : language === 'en' 
              ? 'We will select logistics for your tasks — quickly and profitably or officially with documents'
              : '我们将为您的任务选择物流 — 快速且有利可图，或正式且有文件'}
          </p>
          <RequestForm source={language === 'ru' ? "Логистика" : language === 'en' ? "Logistics" : "物流"} />
        </div>
      </section>
    </div>
  );
}

export default LogisticsPage;